import { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Search, Calendar, TrendingUp, Loader2, Trash2, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import Card, { CardBody } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import Tooltip from '../components/ui/Tooltip';
import CompanyKPIBadges from '../components/financial/sections/CompanyKPIBadges';
import { useAllSummary } from '../components/financial/hooks/useCompanyFinancials';
import { standaloneCompaniesAPI, indiaCompanyAPI, ukCompanyAPI } from '../lib/api';
import { SESSION_CACHE } from '../lib/queryCache';
import type { Company } from './companyTypes';
import {
  REGION_BADGE,
  canRefresh,
  deriveCompanyRegion,
  hasPdfChain,
  resolveRegionBadge,
} from './companyProvenance';


const PENDING_JOB_STATUSES = new Set(['queued', 'in_progress']);

export default function CompanyResearch() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // A Set, not a single id: refreshes are independent async jobs and the user
  // can kick several off in a row. A scalar `regeneratingId` let the second
  // click overwrite the first, so the first company's card lost its "Refreshing…"
  // state (and re-enabled its buttons) while its request was still in flight.
  const [regeneratingIds, setRegeneratingIds] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState<{ companyId: string; companyName: string } | null>(null);
  const handleOpenResearch = useCallback(() => navigate('/companies/research'), [navigate]);

  // Companies list.
  //
  // Deliberately NOT the SESSION_CACHE preset used elsewhere: that sets
  // staleTime: Infinity + refetchOnMount: false, which meant navigating back
  // here after researching a company re-rendered the cached rows and never
  // re-hit GET /api/companies — the new company stayed invisible until a hard
  // reload. Research is asynchronous, so the row often does not exist yet when
  // the POST returns; the list has to re-ask on arrival.
  //
  // `refetchOnMount: 'always'` revalidates on every visit while keeping the
  // cached rows on screen (React Query serves cache first, then swaps in the
  // response), so there is no loading flash and no torn empty state.
  const {
    data: companies = [],
    isLoading: loading,
    error: companiesError,
    refetch,
  } = useQuery<Company[]>({
    queryKey: ['companies'],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
      const response = await fetch(`${baseUrl}/companies`);
      if (!response.ok) {
        throw new Error(`Failed to fetch companies: ${response.statusText}`);
      }
      return (await response.json()) as Company[];
    },
    gcTime: SESSION_CACHE.gcTime, // keep rows cached across tab switches
    staleTime: 0,
    refetchOnMount: 'always',
    // While any company still has a job in flight, poll every 10 s until it
    // reports `completed`; polling stops once everything settles, so a quiet
    // list makes no repeat calls.
    refetchInterval: (query) => {
      const rows = (query.state.data as Company[] | undefined) ?? [];
      const hasPending = rows.some(
        (c) => c.latestJobStatus && PENDING_JOB_STATUSES.has(c.latestJobStatus)
      );
      return hasPending ? 10_000 : false;
    },
  });
  const error = companiesError ? (companiesError as Error).message : null;

  const handleDelete = useCallback((e: React.MouseEvent, company: Company) => {
    e.stopPropagation();
    setConfirmDelete({ companyId: company.companyId, companyName: company.companyName });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!confirmDelete) return;
    const { companyId, companyName } = confirmDelete;
    setConfirmDelete(null);
    setDeletingId(companyId);
    try {
      await standaloneCompaniesAPI.deleteCompany(companyId);
      await refetch();
    } catch (err) {
      console.error('Failed to delete company:', err);
    } finally {
      setDeletingId(null);
    }
  }, [confirmDelete, refetch]);

  const handleRegenerate = useCallback(async (e: React.MouseEvent, company: Company) => {
    e.stopPropagation();
    // Not `deriveCompanyRegion(...) === 'PDF'`: that reads the id prefix and
    // would refuse a merged company whose API chain is refreshable.
    if (!canRefresh(company)) {
      toast.error('Refresh is not available for PDF-sourced companies.');
      return;
    }
    const region = deriveCompanyRegion(company);

    setRegeneratingIds((prev) => new Set(prev).add(company.companyId));
    toast.info(`Refreshing financial data for ${company.companyName}…`);
    try {
      if (region === 'US') {
        const res = await standaloneCompaniesAPI.fetchUSFinancialData({
          company_name: company.companyName,
          company_code: company.ticker,
          time_range: '5years',
        });
        const status = String((res as any)?.status || 'success').toLowerCase();
        if (status === 'error') {
          throw new Error((res as any)?.message || 'US financial data refresh failed');
        }
      } else if (region === 'UK') {
        const res = await ukCompanyAPI.triggerResearch({
          company_name: company.companyName,
          company_code: company.cik,
          data_type: 'annual',
          time_range: '5years',
        });
        if (res?.status === 'error') {
          throw new Error(res?.message || 'UK financial data refresh failed');
        }
      } else {
        const res = await indiaCompanyAPI.triggerResearch({
          company_name: company.companyName,
          data_type: 'consolidated',
        });
        const triggerStatus = (res?.status || '').toLowerCase();
        if (['failed', 'error', 'not_found', 'failure'].includes(triggerStatus) || (res as any)?.error) {
          throw new Error((res as any)?.error || res?.message || 'India financial data refresh failed');
        }
      }
      toast.success(`Financial data refreshed for ${company.companyName}`);
      // Bust the cached financial queries (KPI badges read from the bulk summary,
      // keyed under 'financial') so the card reflects the freshly-ingested data
      // instead of the stale, still-fresh-by-staleTime cache.
      await queryClient.invalidateQueries({ queryKey: ['financial'] });
      await refetch();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Refresh failed';
      console.error('Failed to refresh company:', err);
      toast.error(msg);
    } finally {
      setRegeneratingIds((prev) => {
        const next = new Set(prev);
        next.delete(company.companyId);
        return next;
      });
    }
  }, [refetch]);

  // Hide companies that have no financial data and are no longer actively
  // processing — this covers UK PARTIAL results where the company node is
  // created but no iXBRL/XML could be parsed (e.g. BP files PDF-only).
  // Companies with a pending job (queued / in_progress) are always shown so
  // the "Processing…" badge is visible while the pipeline is still running.
  const companiesWithData = companies.filter(
    (c) => c.reportCount > 0 || PENDING_JOB_STATUSES.has(c.latestJobStatus ?? '')
  );

  const filteredCompanies = companiesWithData.filter(
    (c) =>
      c.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.ticker && c.ticker.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Bulk-fetch KPI summaries for all non-pending companies in one request.
  // Memoized against the `companies` state so the array (and therefore the
  // useAllSummary query key) stays referentially stable across unrelated
  // re-renders (search typing, modals) — otherwise the /companies/bulk-summary
  // request re-fires whenever the component re-renders.
  const nonPendingNames = useMemo(
    () =>
      companies
        .filter((c) => c.reportCount > 0 || PENDING_JOB_STATUSES.has(c.latestJobStatus ?? ''))
        .filter((c) => !c.latestJobStatus || !PENDING_JOB_STATUSES.has(c.latestJobStatus))
        .map((c) => c.companyName),
    [companies]
  );
  const { data: bulkSummaryData, isLoading: bulkSummaryLoading } = useAllSummary(
    nonPendingNames.length ? nonPendingNames : null
  );

  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900 flex items-center gap-3">
              <Building2 className="h-8 w-8 text-primary-600" />
              Company Research
            </h1>
            <p className="text-neutral-600 mt-2">
              Independent company analysis powered by trusted regulatory filings and AI
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="primary" size="lg" onClick={handleOpenResearch}>
              <Plus className="h-5 w-5 mr-2" />
              Research New Company
            </Button>
          </div>
        </div>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex gap-4">
            <div className="flex-1">
              {loading ? (
                <div className="h-10 bg-neutral-100 rounded-lg animate-pulse w-full" />
              ) : (
                <Input
                  placeholder="Search companies by name or ticker..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftIcon={<Search className="h-4 w-4 text-neutral-400" />}
                />
              )}
            </div>
            {loading ? (
              <div className="h-10 w-28 bg-neutral-100 rounded-lg animate-pulse shrink-0" />
            ) : (
              <div className="flex items-center text-sm text-neutral-500 shrink-0">
                {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Company list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardBody>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-12 h-12 rounded-xl bg-neutral-200 animate-pulse shrink-0" />
                    <div className="flex-1 space-y-2.5">
                      <div className="h-4 bg-neutral-200 rounded-md animate-pulse w-2/5" />
                      <div className="h-3 bg-neutral-100 rounded-md animate-pulse w-1/3" />
                      <div className="flex gap-2">
                        <div className="h-5 bg-neutral-100 rounded-full animate-pulse w-16" />
                        <div className="h-5 bg-neutral-100 rounded-full animate-pulse w-20" />
                        <div className="h-5 bg-neutral-100 rounded-full animate-pulse w-12" />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1.5 shrink-0">
                    <div className="h-3 bg-neutral-100 rounded animate-pulse w-20" />
                    <div className="h-4 bg-neutral-200 rounded animate-pulse w-16" />
                  </div>
                </div>
                <div className="flex items-center gap-6 mt-4">
                  <div className="h-3 bg-neutral-100 rounded animate-pulse w-20" />
                  <div className="h-3 bg-neutral-100 rounded animate-pulse w-24" />
                  <div className="h-3 bg-neutral-100 rounded animate-pulse w-28" />
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card>
          <CardBody className="text-center py-12">
            <div className="text-error-600 mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Failed to Load Companies</h3>
            <p className="text-neutral-600 mb-6">{error}</p>
            <Button variant="primary" onClick={() => window.location.reload()}>Retry</Button>
          </CardBody>
        </Card>
      ) : filteredCompanies.length === 0 ? (
        <Card>
          <CardBody className="text-center py-12">
            <Building2 className="h-16 w-16 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">
              {companies.length === 0 ? 'No Companies Yet' : 'No Results Found'}
            </h3>
            <p className="text-neutral-600 mb-6">
              {companies.length === 0
                ? 'Start by researching your first company — US (SEC), UK (Companies House) or India (BSE/NSE) supported'
                : 'Try adjusting your search query'}
            </p>
            {companies.length === 0 && (
              <Button variant="primary" onClick={handleOpenResearch}>
                <Plus className="h-4 w-4 mr-2" />
                Research New Company
              </Button>
            )}
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredCompanies.map((company) => {
            const isPending = PENDING_JOB_STATUSES.has(company.latestJobStatus ?? '');
            const isDeleting = deletingId === company.companyId;
            const isRegenerating = regeneratingIds.has(company.companyId);
            const region = deriveCompanyRegion(company);
            // Which exchange to badge, and whether to badge a PDF chain, are
            // now two separate questions — see `resolveRegionBadge`.
            const regionBadge = resolveRegionBadge(company);
            const showsPdfBadge = hasPdfChain(company);
            const showsRefresh = canRefresh(company);
            const identifierLabel = region === 'UK' ? 'Company No' : 'CIK';
            const isBusy = isPending || isDeleting || isRegenerating;
            // Latest-year statement completeness (from the bulk summary). When a
            // required statement is missing we don't surface financial figures
            // here — the rest of the app reports the company as unanalysable, so
            // showing Latest/Revenue alongside that reads as a contradiction.
            const rowSummary = bulkSummaryData?.summaries?.[company.companyName];
            const missingBS = rowSummary?.has_balance_sheet === false;
            const missingPL = rowSummary?.has_profit_loss === false;
            const statementsIncomplete = missingBS || missingPL;
            const missingLabel = missingBS && missingPL
              ? 'No Balance Sheet or Profit & Loss — analysis unavailable'
              : missingBS
                ? 'No Balance Sheet for the latest year — analysis unavailable'
                : 'No Profit & Loss for the latest year — analysis unavailable';
            return (
              <Card
                key={company.companyId}
                className={isBusy
                  ? 'opacity-60 cursor-not-allowed'
                  : 'hover:shadow-lg transition-shadow cursor-pointer'}
                onClick={isBusy ? undefined : () => navigate(`/companies/${company.companyId}`)}
              >
                <CardBody>
                  <div className="flex items-start justify-between gap-6">
                    {/* Left: company info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                          <Building2 className="h-6 w-6 text-primary-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-lg font-semibold text-neutral-900">{company.companyName}</h3>
                          <div className="flex items-center gap-3 text-sm text-neutral-600 flex-wrap">
                            {company.ticker && (
                              <span className="font-medium text-primary-600">{company.ticker}</span>
                            )}
                            {company.cik && (
                              <span>{identifierLabel}: {company.cik}</span>
                            )}
                            {/* A merged company has BOTH provenances, so both
                                are shown: one row per company, not one row per
                                source. `resolveRegionBadge` returns null only
                                when no exchange exists (a PDF-only company),
                                so the two badges never duplicate each other. */}
                            {regionBadge && (
                              <span className="px-2 py-0.5 bg-neutral-100 rounded text-xs">
                                {REGION_BADGE[regionBadge]}
                              </span>
                            )}
                            {showsPdfBadge && (
                              <span
                                className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded text-xs font-medium"
                                title="Includes data extracted from an uploaded PDF"
                              >
                                PDF
                              </span>
                            )}
                            {isPending && (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Processing…
                              </span>
                            )}
                            {isRegenerating && !isPending && (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Refreshing…
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {!isPending && (
                        <CompanyKPIBadges
                          companyName={company.companyName}
                          summary={bulkSummaryData?.summaries?.[company.companyName]}
                          loading={bulkSummaryLoading}
                        />
                      )}
                      {!isPending && statementsIncomplete ? (
                        // Say why the company can't be analysed instead of listing
                        // figures the rest of the app is simultaneously reporting
                        // as unavailable.
                        <div className="flex items-center gap-2 mt-3 text-sm">
                          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                          <span className="text-amber-700">{missingLabel}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-6 mt-3 text-sm">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-neutral-400" />
                            <span className="text-neutral-600">{company.reportCount} reports</span>
                          </div>
                          {!isPending && (
                            <>
                              <div className="flex items-center gap-2">
                                <TrendingUp className="h-4 w-4 text-neutral-400" />
                                <span className="text-neutral-600">Latest: {company.latestPeriod}</span>
                              </div>
                              {company.latestRevenue && company.latestRevenue !== '$0.0M' && company.latestRevenue !== '₹0 Cr' && (
                                <div className="text-neutral-600">Revenue: {company.latestRevenue}</div>
                              )}
                            </>
                          )}
                          {isPending && (
                            <span className="text-neutral-500 text-xs">Data is being loaded — please wait</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Right: metadata + delete */}
                    <div className="shrink-0 flex flex-col items-end justify-between self-stretch gap-3">
                      <div className="text-right text-sm text-neutral-500">
                        <div>Last researched</div>
                        <div className="font-medium text-neutral-700">
                          {new Date(company.lastResearchedAt).toLocaleDateString()}
                        </div>
                      </div>
                      {!isPending && (
                        <div className="flex items-center gap-2">
                          {showsRefresh && (
                            <Tooltip content="Refresh financial data" position="top">
                              <button
                                onClick={(e) => handleRegenerate(e, company)}
                                disabled={isRegenerating || isDeleting}
                                aria-label="Refresh financial data"
                                className="px-3 py-2 border border-primary-200 text-primary-600 rounded-lg hover:bg-primary-50 hover:border-primary-300 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isRegenerating
                                  ? <Loader2 className="w-4 h-4 animate-spin" />
                                  : <RefreshCw className="w-4 h-4" />
                                }
                              </button>
                            </Tooltip>
                          )}
                          <Tooltip content="Delete company" position="top">
                            <button
                              onClick={(e) => handleDelete(e, company)}
                              disabled={isDeleting || isRegenerating}
                              aria-label="Delete company"
                              className="px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 hover:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isDeleting
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Trash2 className="w-4 h-4" />
                              }
                            </button>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete confirmation modal */}
      <Modal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Delete Company"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm transition-colors"
            >
              Delete
            </button>
          </>
        }
      >
        <div className="flex gap-4">
          <div className="shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-neutral-900 mb-1">
              Delete &ldquo;{confirmDelete?.companyName}&rdquo;?
            </p>
            <p className="text-sm text-neutral-500">
              This will permanently remove the company and all its financial reports from the database. This action cannot be undone.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
